import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Circle, Truck, XCircle, RotateCcw, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, inr, offerNameLabel } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { statusLabel } from "@/lib/status";
import { useSeo } from "@/lib/seo";

const FLOW = ["pending_payment", "paid", "processing", "shipped", "out_for_delivery", "delivered"];

export default function OrderDetailPage() {
  useSeo({ title: "Order Details | Soonyoung", noindex: true });
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const { isAuthed, setLoginOpen, authReady } = useStore();
  const [order, setOrder] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [busy, setBusy] = useState(false);
  const success = params.get("status") === "success";

  const load = useCallback(async () => {
    try {
      const { data: o } = await api.get(`/ecom/orders/${orderId}`);
      setOrder(o?.order || o);
    } catch (e) { toast.error(errMsg(e)); return; }
    // tracking may 404 until the order ships — never block the page on it
    try {
      const { data: t } = await api.get(`/ecom/orders/${orderId}/tracking`);
      setTracking(t);
    } catch { setTracking(null); }
  }, [orderId]);

  useEffect(() => {
    if (!isAuthed) return;
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [isAuthed, load]);

  if (authReady && !isAuthed) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-7xl flex-col items-center justify-center px-4 pt-28 text-center" data-testid="order-login-prompt">
        <h1 className="font-display text-3xl text-cream">Sign in to view this order</h1>
        <button data-testid="order-login-btn" onClick={() => setLoginOpen(true)} className="mt-6 rounded-full bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest">Sign in with OTP</button>
      </div>
    );
  }

  if (!order) {
    return <div className="mx-auto max-w-5xl px-4 pt-32"><div className="h-64 animate-pulse rounded-3xl bg-moss" /></div>;
  }

  const stepIdx = FLOW.indexOf(order.status);
  const canCancel = ["pending_payment", "paid", "processing"].includes(order.status);
  const canReturn = order.status === "delivered";

  const act = async (kind) => {
    const reason = window.prompt(kind === "cancel" ? "Reason for cancellation?" : "Reason for return?");
    if (!reason) return;
    setBusy(true);
    try {
      await api.post(`/ecom/orders/${orderId}/${kind === "cancel" ? "cancel" : "return-request"}`, { reason });
      toast.success(kind === "cancel" ? "Order cancelled" : "Return requested");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <section data-testid="order-detail-page" className="mx-auto max-w-5xl px-4 pb-20 pt-28 sm:px-6 sm:pt-32">
      <Link to="/orders" className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-sage hover:text-gold" data-testid="back-to-orders">
        <ArrowLeft className="h-3 w-3" /> All orders
      </Link>

      {success && (
        <div className="mt-6 flex items-center gap-3 rounded-2xl border border-gold/40 bg-moss px-5 py-4" data-testid="order-success-banner">
          <CheckCircle2 className="h-5 w-5 text-gold" />
          <p className="text-sm text-cream">Thank you! Your order is confirmed and being prepared.</p>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{order.order_number}</p>
          <h1 className="mt-2 font-display text-3xl tracking-tight text-cream sm:text-4xl" data-testid="order-status-heading">{statusLabel(order.status)}</h1>
        </div>
        <div className="flex gap-3">
          {canCancel && (
            <button data-testid="cancel-order-btn" onClick={() => act("cancel")} disabled={busy} className="flex items-center gap-2 rounded-full border border-gold/50 px-5 py-2.5 font-mono text-[10px] uppercase tracking-widest text-gold hover:bg-gold hover:text-forest disabled:opacity-50">
              <XCircle className="h-3.5 w-3.5" /> Cancel
            </button>
          )}
          {canReturn && (
            <button data-testid="return-order-btn" onClick={() => act("return")} disabled={busy} className="flex items-center gap-2 rounded-full border border-champagne/50 px-5 py-2.5 font-mono text-[10px] uppercase tracking-widest text-cream hover:border-gold hover:text-gold disabled:opacity-50">
              <RotateCcw className="h-3.5 w-3.5" /> Return
            </button>
          )}
        </div>
      </div>

      <div className="mt-8 rounded-3xl border border-champagne/40 bg-pine p-6" data-testid="order-progress">
        <div className="flex items-center">
          {FLOW.map((s, i) => (
            <div key={s} className="flex flex-1 items-center last:flex-none">
              <div className="flex flex-col items-center">
                {i <= stepIdx ? <CheckCircle2 className="h-5 w-5 text-gold" /> : <Circle className="h-5 w-5 text-champagne" />}
                <span className={`mt-2 hidden text-center font-mono text-[8px] uppercase tracking-widest sm:block ${i <= stepIdx ? "text-cream" : "text-fern"}`}>{statusLabel(s)}</span>
              </div>
              {i < FLOW.length - 1 && <div className={`mx-1 h-px flex-1 ${i < stepIdx ? "bg-gold" : "bg-champagne/40"}`} />}
            </div>
          ))}
        </div>
        {tracking?.awb_code && (
          <p className="mt-5 flex items-center gap-2 border-t border-champagne/30 pt-4 text-xs text-sage" data-testid="tracking-awb">
            <Truck className="h-4 w-4 text-gold" /> {tracking.courier_name} · AWB <b className="font-mono text-cream">{tracking.awb_code}</b>
          </p>
        )}
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_340px]">
        <ul className="divide-y divide-champagne/30 rounded-3xl border border-champagne/40 bg-pine px-5" data-testid="order-items">
          {(order.items || []).map((it, i) => (
            <li key={it.product_id || i} className="flex items-center justify-between gap-4 py-4">
              <div>
                <p className="font-display text-base text-cream">{it.name}</p>
                <p className="font-mono text-[10px] text-fern">Qty {it.qty} · {inr(it.selling_price)} each</p>
                {it.offer_name && (
                  <p data-testid={`order-offer-${i}`} className="mt-1 inline-flex w-fit items-center rounded-full bg-black px-2.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-white">
                    Offer · {offerNameLabel(it.offer_name)}{it.offer_discount > 0 ? ` · saved ${inr(it.offer_discount)}` : ""}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="font-mono text-sm text-cream">{inr(it.line_total)}</p>
                {it.list_price > it.selling_price && (
                  <p className="font-mono text-[10px] text-fern line-through">{inr(it.list_price * it.qty)}</p>
                )}
              </div>
            </li>
          ))}
        </ul>

        <div className="space-y-6">
          <div className="rounded-3xl border border-champagne/40 bg-pine p-6" data-testid="order-totals">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Paid</p>
            <p className="mt-2 font-display text-3xl text-cream">{inr(order.grand_total)}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-fern">{order.payment_status?.replace(/_/g, " ")}</p>
            {(order.offer_discount ?? order.totals?.offer_discount ?? 0) > 0 && (
              <p className="mt-3 rounded-xl bg-gold/10 px-3 py-2 text-xs font-bold text-gold" data-testid="order-offer-savings">
                Offer savings {inr(order.offer_discount ?? order.totals?.offer_discount)}
              </p>
            )}
          </div>
          {(order.shipment || order.quoted_courier_name || order.estimated_delivery_days || order.shipping_note) && (
            <div className="rounded-3xl border border-champagne/40 bg-pine p-6" data-testid="order-shipment">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Shipment</p>
              {order.quoted_courier_name && (
                <p className="mt-2 text-sm font-bold text-cream" data-testid="order-courier">{order.quoted_courier_name}</p>
              )}
              {order.estimated_delivery_days > 0 && (
                <p className="mt-1 text-xs text-sage" data-testid="order-eta">
                  Estimated delivery in {order.estimated_delivery_days} day{order.estimated_delivery_days === 1 ? "" : "s"}
                </p>
              )}
              {order.shipment?.parcel_weight_kg > 0 && (
                <p className="mt-1 text-xs text-sage" data-testid="order-parcel-weight">
                  Parcel weight: {order.shipment.parcel_weight_kg} kg{order.shipment.has_estimated_weights ? " (estimated)" : ""}
                </p>
              )}
              {order.shipping_note && (
                <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.15em] text-fern" data-testid="order-shipping-note">{order.shipping_note}</p>
              )}
            </div>
          )}
          {order.delivery_address && (
            <div className="rounded-3xl border border-champagne/40 bg-pine p-6" data-testid="order-address">              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Delivering to</p>
              <p className="mt-2 font-display text-base text-cream">{order.delivery_address.name}</p>
              <p className="mt-1 text-xs leading-relaxed text-sage">
                {order.delivery_address.line1}{order.delivery_address.line2 ? `, ${order.delivery_address.line2}` : ""}, {order.delivery_address.city} {order.delivery_address.pincode}, {order.delivery_address.state}
              </p>
            </div>
          )}
          {order.timeline?.length > 0 && (
            <div className="rounded-3xl border border-champagne/40 bg-pine p-6" data-testid="order-timeline">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Timeline</p>
              <ul className="mt-3 space-y-3">
                {[...order.timeline].reverse().map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-xs">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                    <div>
                      <p className="text-cream">{statusLabel(t.status)}</p>
                      <p className="text-fern">{t.at ? new Date(t.at).toLocaleString("en-IN") : ""}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
